import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { ROLES } from '../../constants/roles';
import { useTheme } from '@mui/material/styles';
import {
  getUnreadNotifications,
  invalidateUnreadNotifications,
} from '../../lib/notificationsUnreadCache';

const SETTINGS_ALLOWED_ROLES: string[] = [ROLES.SUPER_ADMIN, ROLES.PRIMARY, ROLES.PARTNER];
const NOTIFICATIONS_ALLOWED_ROLES: string[] = [ROLES.SUPER_ADMIN, ROLES.PRIMARY, ROLES.PARTNER];

type TopBarProps = {
  onMobileMenuToggle?: () => void;
};

const TopBar: React.FC<TopBarProps> = ({ onMobileMenuToggle }) => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const barRef = useRef<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const fetchUnreadCount = useCallback(async () => {
    if (!auth.token) {
      setUnreadCount(0);
      return;
    }

    try {
      const unread = await getUnreadNotifications();
      setUnreadCount(unread.length);
    } catch (err) {
      console.error('Unread notifications fetch error:', err);
    }
  }, [auth.token]);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const setTopBarHeightVar = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--app-topbar-height', `${h}px`);
      }
    };

    setTopBarHeightVar();

    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => setTopBarHeightVar());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleChanged = () => {
      invalidateUnreadNotifications();
      getUnreadNotifications({ force: true })
        .then((unread) => setUnreadCount(unread.length))
        .catch((err) => {
          console.error('Unread notifications fetch error:', err);
        });
    };

    window.addEventListener('notifications:changed', handleChanged);
    return () => {
      window.removeEventListener('notifications:changed', handleChanged);
    };
  }, [fetchUnreadCount]);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);
  const handleProfileNavigate = () => {
    handleClose();
    navigate('/profile');
  };
  const handleSettingsNavigate = () => {
    handleClose();
    navigate('/settings');
  };
  const canAccessSettings = SETTINGS_ALLOWED_ROLES.includes(auth.user?.role ?? '');
  const canAccessNotifications = NOTIFICATIONS_ALLOWED_ROLES.includes(auth.user?.role ?? '');

  return (
    <AppBar
      ref={barRef}
      position="sticky"
      sx={{ backgroundColor: '#daffd2', zIndex: (theme) => theme.zIndex.drawer + 1 }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minWidth: 0, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: { xs: 0.5, sm: 1 } }}>
          {isMobile ? (
            <IconButton aria-label="Open navigation menu" onClick={onMobileMenuToggle}>
              <MenuIcon />
            </IconButton>
          ) : null}
          <Avatar
            sx={{
              width: { xs: 120, sm: 160, md: 245 },
              height: { xs: 48, sm: 64, md: 100 },
              backgroundColor: 'transparent',
            }}
          >
            <img src="/assets/img/logo.png" alt="Velora logo" style={{ width: '100%' }} />
          </Avatar>
        </Box>

        {auth.user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            {canAccessNotifications ? (
              <IconButton aria-label="Notifications" onClick={() => navigate('/notifications')}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsNoneOutlinedIcon />
                </Badge>
              </IconButton>
            ) : null}

            <Button
              onClick={handleMenuClick}
              startIcon={
                <Avatar sx={{ bgcolor: '#0e5321', color: '#daffd2' }}>
                  {auth.user.email[0].toUpperCase()}
                </Avatar>
              }
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 0.2s ease-in-out',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              }
              aria-label="Account menu"
              sx={{
                color: 'black',
                textTransform: 'none',
                minWidth: 0,
                px: { xs: 0.75, sm: 1.5 },
              }}
            >
              {!isMobile ? (
                <Typography component="span" noWrap sx={{ maxWidth: 260 }}>
                  {auth.user.email}
                </Typography>
              ) : null}
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              <MenuItem onClick={handleProfileNavigate}>My Profile</MenuItem>
              {canAccessSettings ? <MenuItem onClick={handleSettingsNavigate}>Settings</MenuItem> : null}
              <MenuItem
                onClick={() => {
                  handleClose();
                  logout();
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Button href="/login" color="inherit">
            Login
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
